/**
 * Page snapshot for OCR upload (M5 spec §3.1). Renders paper + committed ink
 * + typed text to an offscreen canvas and encodes it as a PNG:
 *
 * - ALWAYS light theme — dark ink on light paper is what Vision OCRs best,
 *   regardless of the UI theme.
 * - NO template — grid/ruled lines only confuse OCR.
 * - NO images / PDF underlay — v1 search scope is handwriting + typed text.
 * - Long side capped at SNAPSHOT_MAX_SIDE (1400px); ink is re-rendered as
 *   vectors at that scale, so small pages upscale sharply rather than blur.
 *
 * Returns `{ blob, scale }` where `scale` = snapshot px per world px: the
 * server divides Vision's pixel boxes by it to get world coords. NOTE: boxes
 * come back relative to the snapshot's world origin — exact for fixed pages
 * (origin 0,0); on infinite pages the content-bbox origin shifts them, an
 * accepted v1 limitation (search text/deep link stay correct, only highlight
 * rects drift).
 *
 * Framework-free; canvas creation is injectable so tests supply a stub
 * (jsdom has no real 2D context or toBlob).
 */
import type { Stroke } from './stroke';
import { rectsIntersect, type Rect } from './stroke';
import { buildRibbon, fillRibbon } from './ribbon';
import { drawTextBox, textBoxRect, textBoxFont } from './text';
import { resolveInk, isHighlight, paperColor } from '../theme/tokens';
import { thumbLayout } from './thumbnail';
import { b64ToBytes } from '../sync/protocol';
import type { Page, TextBox } from '../store/types';

/** Max snapshot long side in px — plenty for Vision, well under the 2MB PNG cap. */
export const SNAPSHOT_MAX_SIDE = 1400;

export interface SnapshotOpts {
  /** Long-side cap override (tests). Default SNAPSHOT_MAX_SIDE. */
  maxSide?: number;
  /** Injectable canvas factory (tests). Default document.createElement. */
  createCanvas?: (w: number, h: number) => HTMLCanvasElement;
}

export interface PageSnapshot {
  blob: Blob;
  scale: number;
}

function defaultCanvas(w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

/** PNG-encode a canvas: prefer toBlob, fall back to toDataURL (old Safari / stubs). */
function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  if (typeof canvas.toBlob === 'function') {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('snapshot: canvas produced no PNG data'))),
        'image/png',
      );
    });
  }
  const dataUrl = canvas.toDataURL('image/png');
  const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  // Copy into a fresh Uint8Array so TS sees a plain ArrayBuffer-backed view.
  const bytes = new Uint8Array(b64ToBytes(b64));
  return Promise.resolve(new Blob([bytes], { type: 'image/png' }));
}

/**
 * Render a page's OCR snapshot. Pass ACTIVE strokes/text boxes (deleted ones
 * are skipped defensively anyway). Pass order matches the tile renderer:
 * highlighter under text under pen.
 */
export async function renderPageSnapshot(
  page: Page,
  strokes: Stroke[],
  textBoxes: TextBox[],
  opts: SnapshotOpts = {},
): Promise<PageSnapshot> {
  const maxSide = opts.maxSide ?? SNAPSHOT_MAX_SIDE;
  const { world, w, h, scale } = thumbLayout(page, strokes, maxSide, maxSide);
  const canvas = (opts.createCanvas ?? defaultCanvas)(w, h);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('snapshot: no 2d context');

  ctx.fillStyle = paperColor('light');
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(-world.x, -world.y);
  drawStrokePass(ctx, strokes, world, 'highlighter');
  for (const box of textBoxes) {
    if (box.deletedAt) continue;
    ctx.save();
    ctx.font = textBoxFont(box);
    const rect = textBoxRect(box, (s) => ctx.measureText(s).width);
    ctx.restore();
    if (!rectsIntersect(rect, world)) continue;
    drawTextBox(ctx, box, 'light');
  }
  drawStrokePass(ctx, strokes, world, 'pen');
  ctx.restore();

  const blob = await canvasToPng(canvas);
  return { blob, scale };
}

function drawStrokePass(ctx: CanvasRenderingContext2D, strokes: Stroke[], world: Rect, pass: 'highlighter' | 'pen'): void {
  for (const s of strokes) {
    if (s.tool !== pass || s.deletedAt) continue;
    if (!rectsIntersect(s.bbox, world)) continue;
    ctx.save();
    if (isHighlight(s.color) || s.tool === 'highlighter') ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = resolveInk(s.color, 'light');
    fillRibbon(ctx, buildRibbon(s.points, s.baseWidth));
    ctx.restore();
  }
}
